"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  onlyPriceChanged,
  onlySearchChanged,
  searchWithFilters,
  type FilterQueryKey,
  type FilterQueryState,
} from "@/lib/filter-query";

// Schrijft de gekozen filters in het adres (MOW-FILTERLINKS-OKT26). Alleen aanroepen vanuit de handler die de
// bezoeker aanstuurt (changeFilters), niet vanuit een effect: een effect draait ook bij het eerste renderen en
// zou een gedeelde link meteen herschrijven (zelfde les als B1 in usePagePagination).
//
// - History API met replaceState: geen nieuwe historie-ingang en geen serververzoek (Next koppelt dit aan
//   useSearchParams). Het adres wordt gelezen op het moment van schrijven, niet uit de laatste render: Next
//   verwerkt history.* via een startTransition, dus useSearchParams loopt even achter.
// - Typen in het zoekveld en slepen aan de prijsschuif worden gedempt (standaard 400 ms): niet bij elke toets
//   of stap een adreswijziging, want een adreswijziging kan in de analytics als paginaweergave meetellen. Elke
//   andere wijziging schrijft meteen en neemt een wachtende zoekterm of prijs mee.
// - Alle overige parameters (pagina, sale, nieuw, utm_*, gclid, ...) blijven byte voor byte staan.

export interface FilterUrlWriterOptions {
  /** Sleutels die de pagina zelf bezit (de merkpagina heeft het merk in de route). */
  skip?: ReadonlyArray<FilterQueryKey>;
  /** Wordt aangeroepen met de nieuwe querystring nadat het adres is bijgewerkt. */
  onWritten?: (search: string) => void;
  /** Demping voor typen, in milliseconden. */
  searchDelayMs?: number;
  /** Sortering waarop de pagina zelf opent als ze afwijkt van "relevance" (/shop?nieuw=1: "newest"). */
  defaultSort?: string;
}

export interface FilterUrlWriter {
  /** Zet de filters in het adres (direct, of gedempt als alleen de zoekterm veranderde). */
  write: (next: FilterQueryState) => void;
  /** Annuleert een wachtende (gedempte) schrijfactie, bijvoorbeeld als het adres door iets anders is gewijzigd. */
  cancelPending: () => void;
}

export function useFilterUrlWriter(options: FilterUrlWriterOptions = {}): FilterUrlWriter {
  const optionsRef = useRef<FilterUrlWriterOptions>(options);
  useEffect(() => {
    optionsRef.current = options;
  });
  const previous = useRef<FilterQueryState | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPending = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(() => cancelPending, [cancelPending]);

  const write = useCallback(
    (next: FilterQueryState) => {
      const apply = (state: FilterQueryState) => {
        try {
          const { pathname, search, hash } = window.location;
          const newSearch = searchWithFilters(search, state, {
            skip: optionsRef.current.skip,
            defaultSort: optionsRef.current.defaultSort,
          });
          if (newSearch === search) return;
          window.history.replaceState(null, "", pathname + newSearch + hash);
          optionsRef.current.onWritten?.(newSearch);
        } catch {
          // Zonder History API blijft het adres zoals het is; het filter zelf werkt gewoon.
        }
      };

      const before = previous.current;
      previous.current = next;
      cancelPending();
      if (before && (onlySearchChanged(before, next) || onlyPriceChanged(before, next))) {
        timer.current = setTimeout(() => {
          timer.current = null;
          apply(next);
        }, optionsRef.current.searchDelayMs ?? 400);
      } else {
        apply(next);
      }
    },
    [cancelPending]
  );

  return useMemo(() => ({ write, cancelPending }), [write, cancelPending]);
}
